import loglevel from 'loglevel';

import compact from 'lodash/compact';
import concat from 'lodash/concat';
import each from 'lodash/each';
import flatMap from 'lodash/flatMap';
import split from 'lodash/split';
import get from 'lodash/get';
import keys from 'lodash/keys';
import identity from 'lodash/identity';
import includes from 'lodash/includes';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import pickBy from 'lodash/pickBy';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import fuzzysort from 'fuzzysort';
import lunr from 'lunr';

import {
  getHumanReadablePageContentItemFieldName,
  getHumanIdentifiableAttrForPageContentItem,
  getHumanReadableContentDescription
} from './page_content_utils.js';

import {
  getDisplayName as getSectionDisplayName
} from './sections.js';

import {
  getContentItemByUuid,
  getSectionForContent,
  getSubpageForSection,
  getSubpageById
} from 'zoomforth_codesbane_viewer/reducers/page_entities.selectors.js';

/**
  Given the redux state and a page content item uuid. Return the page content 
  item for the content item uuid and the section containing it.
*/
const getSectionAndPageContentItem = (state, uuid)=> {
  const pageContentItem = getContentItemByUuid(state, uuid);
  const section = getSectionForContent(state, pageContentItem);
  return { section, pageContentItem };
};


const getHumanReadableMenuItemFieldName = (state, id, fieldName)=> {
  if (fieldName !== 'title') {
    throw `Unexpected menuItem Field Name: ${fieldName}`;
  }
  return 'Menu Item';
};

const getContentDescription = (section, pageContentItem)=> {

  const sectionDisplayName = `${getSectionDisplayName(section)} Section`;
  if (includes(['tiles', 'videoplaylist', 'slideshow'], section.type)) {
    return `${sectionDisplayName}: ${getHumanReadableContentDescription(section, pageContentItem)}`;
  }
  return sectionDisplayName;
};


const stripHtml = (htmlString)=> {
  const doc = new DOMParser().parseFromString(htmlString, 'text/html');

  // IE parses HTML differently than others and seems to sometimes set the body
  // to null. I can't explain it, but I have witnessed it in debugging.
  if (isNil(doc.body)) {
    return null;
  }

  return (
    // innerText maintains whitespace (i.e., <br/> --> \n)
    doc.body.innerText || 
    // textContent loses all whitespace but works on IE9
    doc.body.textContent
  );
};

const stripHtmlIgnoreNil = (htmlString)=>
  isNil(htmlString) ? null : stripHtml(htmlString);


const PAGE_CONTENT_KEY_PREPROCESSORS = {
  asset_type: identity,
  caption: stripHtmlIgnoreNil,
  content_type: identity,
  description: stripHtmlIgnoreNil,
  tile_caption: stripHtmlIgnoreNil,
  title: stripHtmlIgnoreNil,
  link_title: stripHtmlIgnoreNil,
  'accordion_data.label': stripHtmlIgnoreNil,
  'accordion_data.content': stripHtmlIgnoreNil,
  // link_description: stripHtml  // Link description *seems* to always match "caption", so ignore it
};

const MENU_ITEM_KEY_PREPROCESSORS = {
  title: identity
};


const makeSearchKeys = ()=>
  uniq(keys(PAGE_CONTENT_KEY_PREPROCESSORS).concat(keys(MENU_ITEM_KEY_PREPROCESSORS)));


const preprocessItem = (preprocessorMap, item)=>
  reduce(preprocessorMap, (accum, func, key)=> ({
    ...accum,
    [key]: func(get(item, key))
  }), {});


const prepareDoc = (doc)=>
  reduce(doc, (accum, val, key)=> ({
    ...accum,
    [key]: fuzzysort.prepare(val)
  }), {});


const preprocessDocuments = (pageEntityState)=> {

  const documents = [];
  const addDoc = (doc)=> {
    documents.push(prepareDoc(doc));
  };

  each(pageEntityState.menuItemsById, (menuItem, id) => {
    const subpage = getSubpageById(pageEntityState, menuItem.appAction.subpageId);
    if(subpage && !subpage.isHidden){
      addDoc({
        type:'menuItem', 
        id,
        uuid: id,
        ...preprocessItem(MENU_ITEM_KEY_PREPROCESSORS, menuItem)
      });
    }
  });

  each(pageEntityState.pageContentItemsByUuid, (pageContentItem, uuid) => {
    const section = getSectionForContent(pageEntityState, uuid);
    if (section) {
      const subpage = getSubpageForSection(pageEntityState, section);

      const isIndexableSubpage = (
        subpage &&
        !subpage.isHidden &&
        (
          subpage.canBeIndexed ||
          isNil(subpage.canBeIndexed) // legacy check since this isn't traditionally set
        )
      );

      if(isIndexableSubpage){
        addDoc({
          type: 'pageContentItem',
          uuid,
          id: uuid,
          ...preprocessItem(PAGE_CONTENT_KEY_PREPROCESSORS, pageContentItem)
        });
      }else if(!subpage){
        loglevel.warn(
          `Ignoring pageContentItem (uuid=${uuid}) in missing subpage (sectionId=${section.id})`
        );  
      }
    } else {
      loglevel.warn(
        `Ignoring pageContentItem (uuid=${uuid}) in missing section`
      );
    }
  });

  return documents;
};

const preprocessDocumentsLunr = (pageEntityState)=> {
  const documents = [];
  const addDoc = (doc)=> {
    documents.push(doc);
  };

  each(pageEntityState.menuItemsById, (menuItem, id) => {
    const subpage = getSubpageById(pageEntityState, menuItem.appAction.subpageId);

    if(subpage && !subpage.isHidden){
      addDoc({
        type:'menuItem', 
        id: `menuItem-${id}`,
        uuid: id,
        ...preprocessItem(MENU_ITEM_KEY_PREPROCESSORS, menuItem)
      });
    }
  });

  each(pageEntityState.pageContentItemsByUuid, (pageContentItem, uuid) => {
    const section = getSectionForContent(pageEntityState, uuid);
    if (section) {
      const subpage = getSubpageForSection(pageEntityState, section);
      if(subpage && !subpage.isHidden){
        addDoc({
          type: 'pageContentItem',
          id: `pageContentItem-${uuid}`,
          uuid,
          ...preprocessItem(PAGE_CONTENT_KEY_PREPROCESSORS, pageContentItem)
        });
      }else if(!subpage){
        loglevel.warn(
          `Ignoring pageContentItem (uuid=${uuid}) in missing subpage (sectionId=${section.id})`
        );  
      }
    } else {
      loglevel.warn(
        `Ignoring pageContentItem (uuid=${uuid}) in missing section`
      );
    }
  });

  return documents;
};

const normalizeFuzzyResults = (fuzzyResults) => (
  reduce(fuzzyResults,
    (ret, fuzzyResult) => {
      const { obj, score } = fuzzyResult;
      const searchKeys = makeSearchKeys();

      const matchFields = keys(pickBy(obj, (value, field) => {
        const valueScore = get(value, 'score');

        return (
             searchKeys.includes(field)
          && !isNil(value)
          && !isNil(valueScore)
          && get(value, 'target')
        );
      }));

      if (!isNil(obj) && matchFields) {
        ret.push({
          type: obj.type.target,
          uuid: get(obj, 'uuid.target'),
          id: get(obj, 'id.target'),
          score,
          matchFields
        });
      }

      return ret;
    }, []
  )
);

const normalizeLunrResults = (lunrResults) => (
  reduce(lunrResults,
    (ret, lunrResult) => {
      const {
        ref,
        score,
        matchData
      } = lunrResult;

      const typeUuid = ref.split('-');
      const type = typeUuid[0];
      const uuid = typeUuid[1];

      if (!isNil(score) && score !== 0) {
        const { metadata } = matchData;
        const matchFields = reduce(metadata,
          (fields, textDataObject) => {
            fields = concat(fields, keys(textDataObject));
            return fields;
          }, []
        );

        if (matchFields) {
          ret.push({
            type,
            uuid,
            id: ref,
            score,
            matchFields
          });
        }
      }

      return ret;
    }, []
  )
);

const searchWithFuzzysort = (
  value,
  documents,
  keys
) => {
  const fuzzyResults = fuzzysort.go(
    value,
    documents,
    {
      keys,
      threshold: -10000
    }
  );

  const normalizedFuzzyResults = normalizeFuzzyResults(fuzzyResults);

  return normalizedFuzzyResults;
};

const searchWithLunr = (
  value,
  keys,
  index
) => {
  const lunrResults = index.query((q) => {
    q.term(
      lunr.tokenizer(value),
      {
        fields: keys
      }
    );
  });

  const normalizedLunrResults = normalizeLunrResults(lunrResults);

  return normalizedLunrResults;
};

const searchText = (
  searchTerm,
  documents,
  keys,
  searchFunctions
) => {

  const joinedResults = flatMap(searchFunctions,
    (searchFn) => (
      searchFn(
        searchTerm,
        documents,
        keys
      )
    )
  );

  const orderedResults = orderBy(joinedResults, ['score'], ['desc']);

  return orderedResults;
};

export default class PageEntityFullTextSearch {

  constructor(pageEntityState) {
    this.documents = preprocessDocuments(pageEntityState);

    this.pageEntityState = pageEntityState;
    this.keys = makeSearchKeys();

    this.idx = lunr(function() {
      const lunrDocuments = preprocessDocumentsLunr(pageEntityState);
      const keys = makeSearchKeys();

      each(keys, (key)=> {
        this.field(key);
      });

      each(lunrDocuments, (doc)=> {
        this.add(doc);
      });
    });

    // We need some way to reference the text bodies because Lunr does not
    // return the matched text.
    this.documentItemsByUuid = reduce(this.documents, (docsById, docBody) => {
      const id = get(docBody, 'uuid.target');
      docsById[id] = docBody;
      return docsById;
    }, {});
  }

  generateResultSet(results, searchValue) {
    const documentItemsByUuid = this.documentItemsByUuid;
    const pageEntityState = this.pageEntityState;

    return map(results, (res)=> {
      const {
        type,
        uuid,
        id,
        score,
        matchFields
      } = res;

      const documentItem = documentItemsByUuid[uuid];

      const { section, pageContentItem } = type === 'pageContentItem' ?
          getSectionAndPageContentItem(pageEntityState, uuid)
        : 
          {};

      const identifyingFieldName = type === 'pageContentItem' ?
          getHumanIdentifiableAttrForPageContentItem(section, pageContentItem)
        :
          {};

      const contentDescription = type === 'pageContentItem' ?
          getContentDescription(section, pageContentItem)
        :
          '';

      return {
        type,
        id,
        uuid,
        score,
        search: searchValue,
        identifyingField:
          (type === 'pageContentItem' && identifyingFieldName) ?
            {
              fieldName: identifyingFieldName,
              displayableFieldName: getHumanReadablePageContentItemFieldName(section, pageContentItem, identifyingFieldName),
              fieldValue: pageContentItem[identifyingFieldName]
            }
          :
            null,
        meta: 
          type === 'pageContentItem' ?
            {
              type,
              section_type: section ? section.type : null,
              content_type: pageContentItem.content_type,
              asset_type: pageContentItem.asset_type,
              description: contentDescription
            }
          :
            {
              type: type
            },
        matches: compact(map(matchFields, (fieldName)=> {

          const matchedText = get(documentItem, `${fieldName}.target`);

          if (isNil(matchedText) || matchedText === '') {
            return null;
          }

          var displayableFieldName = null;

          if (type === 'pageContentItem') {
            displayableFieldName = getHumanReadablePageContentItemFieldName(section, pageContentItem, fieldName);
          }

          // accordions are a little weird since they have nested keys so we
          // need to do some extraction on the field name to make it display in
          // a more human way
          //
          // we could do this in getHumanReadablePageContentItemFieldName but
          // that is used by other functions and we don't want to accidentily
          // mess something up there.
          if (get(section, 'type') === 'accordion') {
            const keyParts = split(fieldName, '.');

            if (includes(keyParts, 'accordion_data') && keyParts.length === 2) {
              displayableFieldName = `Accordion ${keyParts[1]}`;
            }
          }

          if (displayableFieldName === null) {
            displayableFieldName = getHumanReadableMenuItemFieldName(pageEntityState, id, fieldName);
          }

          return {
            score,
            fieldName,
            displayableFieldName,
            matchedText
          };
        }))
      };
    });
  }

  search(value) {
    /**
     * If this class can consume arbitrary search functions then it implies we
     * can run API calls (with timeouts) here as well, although the `searchText`
     * function can be used independently for that.
     * 
     * This function consumes a `value` param and dispatches a search query to
     * each of its search agents (hardcoded to be Lunr and Fuzzysort libs at the
     * moment).
     * 
     * The `searchResults` return value is a collection of normalized objects
     * that matched text in our cached documents.
     **/
    const documents = this.documents;
    const keys = this.keys;

    const lunrSearchFunctionWrapper = function (searchValue, documents, keys) {
      // Wrap this so we can pass the `index` in
      const lunrIndex = this.idx;
      return searchWithLunr(searchValue, keys, lunrIndex);
    }.bind(this);

    const searchResults = searchText(
      value,
      documents,
      keys,
      [
        lunrSearchFunctionWrapper,
        searchWithFuzzysort
      ]
    );

    const filteredResults = uniqBy(searchResults, 'uuid');
    const resultSet = this.generateResultSet(filteredResults, value);

    return resultSet;

  }
}